import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";

import messages from "../../../../../../assets/locale/messages";
import { UIActionModal, UISelectCard } from "../../../../../../components/ui";
import { approveCompany } from "../../../../../../network/apis/companies";
import { getSubscriptionPlans } from "../../../../../../network/apis/subscriptionPlans";
import { getPortalApiErrorMessage } from "../../../../../../utils/companyPortal/eventApi";
import { showErrorMsg, showSuccessMsg } from "../../../../../../utils/Helpers";

import "./ApproveAssignPlanModal.scss";

const planName = (plan, lang) =>
  plan?.[lang === "ar" ? "name_ar" : "name_en"] || plan?.name || "";

/**
 * Approving a registration request: the company only becomes active with a subscription plan.
 * Approving and assigning the plan are one request (PROLINE-4136).
 */
const ApproveAssignPlanModal = ({ open, companyId, companyName, onClose, onApproved }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { shared, registrationReq } = messages[lang];
  const t = registrationReq.approveAssignPlan;
  const [plans, setPlans] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);

  // The plans are asked for each time the modal opens, with no plan selected.
  useEffect(() => {
    if (!open) return undefined;
    setSelectedId(null);
    setSaving(false);
    let cancelled = false;
    // The error is toasted here once, not also by the interceptor.
    getSubscriptionPlans({ handlerEnabled: false })
      .then((response) => {
        if (!cancelled) setPlans(response?.data?.data ?? []);
      })
      .catch((error) => {
        if (!cancelled) showErrorMsg(getPortalApiErrorMessage(error, shared.generalErrMsg));
      });
    return () => {
      cancelled = true;
    };
  }, [open, shared.generalErrMsg]);

  const selected = plans.find((plan) => plan.id === selectedId);

  const approve = async () => {
    setSaving(true);
    try {
      const response = await approveCompany(companyId, selected.id, { handlerEnabled: false });
      const company = response?.data?.data;
      // The toast is built from the response: "[Company] approved", "[Plan] plan assigned. …".
      showSuccessMsg(
        t.successMessage.replace(
          "{plan}",
          company?.subscription_plan?.name || planName(selected, lang),
        ),
        true,
        t.successTitle.replace("{company}", company?.name || companyName),
      );
      onApproved(company);
    } catch (error) {
      // The request stays pending and the modal usable, to approve again.
      const offline = error?.message === "Network Error" && !error?.response;
      if (offline) showErrorMsg(shared.noInternetConnection);
      else showErrorMsg(t.failedMessage, t.failedTitle);
      setSaving(false);
    }
  };

  return (
    <UIActionModal
      open={open}
      className="approve-assign-plan-modal"
      title={t.title}
      onClose={() => !saving && onClose()}
      actions={
        <UIActionModal.Actions
          cancelLabel={shared.cancel}
          confirmLabel={t.confirm}
          confirmDisabled={!selected || saving}
          onCancel={() => !saving && onClose()}
          onConfirm={approve}
        />
      }
    >
      <UIActionModal.Divider />
      <Typography
        component="p"
        className="ui-action-modal__body-text approve-assign-plan-modal__message"
      >
        {t.description.replace("{name}", companyName)}
      </Typography>
      <div
        className="approve-assign-plan-modal__plans"
        role="radiogroup"
        aria-label={t.title}
      >
        {plans.map((plan) => (
          <UISelectCard
            key={plan.id}
            selectionRole="radio"
            size="md"
            title={planName(plan, lang)}
            meta={[
              t.pricePerYear.replace(
                "{price}",
                Number(plan.price_per_year).toLocaleString("en-US"),
              ),
            ]}
            selected={plan.id === selectedId}
            onClick={() => setSelectedId(plan.id)}
          />
        ))}
      </div>
    </UIActionModal>
  );
};

ApproveAssignPlanModal.propTypes = {
  open: PropTypes.bool.isRequired,
  /** The registration (company) to approve. */
  companyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Company name in the current language. */
  companyName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  /** Called with the approved company the backend returns. */
  onApproved: PropTypes.func.isRequired,
};

ApproveAssignPlanModal.defaultProps = {
  companyId: null,
  companyName: "",
};

export default ApproveAssignPlanModal;
